import * as React from 'react';

import * as api from '@gitbook/api-client';
import { CheckBox } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Button,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';

import { apiClient, useCurrentUser } from '../../../../state';

/**
 * Modal to request review of a monetization application.
 */
export function AdsRequestReviewDialog(props: { organizationId: string; site: api.Site }) {
    const { organizationId, site } = props;

    const [compliant, setCompliant] = React.useState(false);

    const user = useCurrentUser();

    /**
     * Steps to submit a site for monetization review.
     * - Site is published
     * - Site has been created more than 7 days ago
     * - Content guidelines are met
     * - User has chosen a topic
     */
    const steps: Array<{
        title: string;
        description: string;
        complete: boolean;
        action?: React.ReactNode;
    }> = [
        {
            title: 'Verify your email address',
            description: 'Email address must be verified before submitting a site for ad review.',
            complete: user !== null && !!user.email && user.emailVerified,
        },

        {
            title: 'Publish your site',
            description: 'Your site must be published and visible to the public.',
            complete: site.visibility === api.SiteVisibility.Public && site.published,
        },
        {
            title: 'Wait 7 days',
            description:
                'Your site must be published for at least 7 days before submitting it for ad review.',
            complete: Date.now() - new Date(site.createdAt).getTime() > 7 * 24 * 60 * 60 * 1000,
        },
        {
            title: 'Content guidelines',
            description:
                'Your site must comply with our content guidelines. It must be respectful, related to the topic you selected, and not infringe any copyrights.',
            complete: compliant,
            action: (
                <div>
                    <CheckBox
                        label="I have read and understood the content guidelines"
                        value={compliant}
                        onValueChange={setCompliant}
                    />
                </div>
            ),
        },
    ];

    const allCompleted = steps.every((step) => step.complete);

    const [, startDialogAction] = useDialogAction();

    const onSubmit = async () => {
        if (!allCompleted) {
            return;
        }

        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.siteAds.updateSiteAdsById(organizationId, site.id, {
                    status: 'in-review',
                });
                await close();
            }, 'Error while submitting your site for ads review');
        });
    };

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Submit your site for ad review</Heading>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-6">
                {steps.map((step, index) => (
                    <div key={index} className="flex gap-4">
                        {step.complete ? (
                            <Badge kind="done" size="medium" icon={Icon.Check} iconOnly />
                        ) : (
                            <Badge kind="secondary" size="medium" icon={Icon.Clock} iconOnly />
                        )}
                        <div className="flex flex-col gap-2">
                            <div>
                                <h3 className="text-base text-ui-heading-small">{step.title}</h3>
                                <p className="text-muted text-ui-base">{step.description}</p>
                            </div>
                            {step.action}
                        </div>
                    </div>
                ))}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Button
                    label="Submit for review"
                    kind="primary"
                    onPress={onSubmit}
                    disabled={!allCompleted}
                />
            </DialogFooter>
        </Dialog>
    );
}
